import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { REVENUE_EVENT_LABEL, REVENUE_EVENTS, REVENUE_PROVIDER_LABEL, type RevenueDelivery, type RevenueDeliveryStatus, type RevenueProvider, type RevenueSource } from "@metrickle/schema/constants";
import { INSTALL_METHOD_LABEL, REVENUE_SNIPPETS, type InstallMethod } from "@metrickle/schema/install";
import { api, useApiMutation, useApp } from "../lib/api";
import { useCan } from "../lib/access";
import { fmtAgo, fmtMoney, fmtNumber } from "../lib/format";
import { track } from "../lib/track";
import { ActionMenu, Ago, Badge, Button, CardSection, Checkbox, CodeBlock, Disclosure, ErrorState, Field, InlineCode, Input, Modal, TabBar, Table, TableScroll, notifyError, toaster, useConfirm, Skeleton, type ActionMenuItem } from "./ui";

/**
 * Integrations page: revenue from the app's own Stripe account and RevenueCat project. Purchases,
 * refunds and cancels arrive server-side on the person the SDK identified, so net task success,
 * revenue kept and a case's money lost need no custom events.
 */

const PROVIDERS: RevenueProvider[] = ["stripe", "revenuecat"];

const BLURB: Record<RevenueProvider, string> = {
  stripe: "Payments, refunds and subscription cancels from your Stripe account.",
  revenuecat: "In-app purchases, renewals, refunds and cancels from App Store and Google Play, through RevenueCat.",
};

/** Which SDK snippets show how to pass the user id, per provider. */
const SNIPPET_METHODS: Record<RevenueProvider, InstallMethod[]> = {
  stripe: ["npm", "script", "http"],
  revenuecat: ["reactNative", "ios", "android", "flutter"],
};

const STRIPE_EVENTS = `checkout.session.completed
checkout.session.async_payment_succeeded
invoice.paid
payment_intent.succeeded
charge.refunded
customer.subscription.created
customer.subscription.updated
customer.subscription.deleted`;

const STATUS_LABEL: Record<RevenueDeliveryStatus, string> = {
  recorded: "Recorded",
  held: "Waiting for the person",
  unmatched: "No person matched",
  ignored: "Skipped",
  failed: "Failed",
};
const STATUS_TONE: Record<RevenueDeliveryStatus, "good" | "bad" | "neutral"> = { recorded: "good", held: "neutral", unmatched: "bad", ignored: "neutral", failed: "bad" };

export const useRevenueSources = (appId: string) => useQuery({ queryKey: ["revenue", appId], queryFn: () => api<RevenueSource[]>(`/apps/${appId}/revenue`) });

/** One line on whether events are arriving, for the row and the "check" button. */
export function sourceStatus(s: RevenueSource | undefined): { text: string; tone: "good" | "bad" | "neutral" } {
  if (!s) return { text: "Not connected", tone: "neutral" };
  if (!s.hasApiKey && !s.hasWebhook) return { text: "Setup not finished", tone: "bad" };
  if (s.lastRejected && (!s.lastEventAt || s.lastRejected.at > s.lastEventAt)) return { text: `Refused ${fmtAgo(s.lastRejected.at)}`, tone: "bad" };
  if (s.backfill?.status === "failed") return { text: "Backfill stopped", tone: "bad" };
  if (!s.lastEventAt) return { text: s.backfill?.status === "running" ? "Backfilling…" : "Waiting for the first event", tone: "neutral" };
  return { text: `Last event ${fmtAgo(s.lastEventAt)}`, tone: "good" };
}

type Dialog = { kind: "setup"; provider: RevenueProvider } | { kind: "log"; provider: RevenueProvider } | null;

export function RevenueSection({ appId }: { appId: string }) {
  const can = useCan(appId);
  const list = useRevenueSources(appId);
  const [dialog, setDialog] = useState<Dialog>(null);
  const close = () => setDialog(null);
  const by = new Map((list.data ?? []).map((s) => [s.provider, s]));
  const current = dialog ? by.get(dialog.provider) : undefined;

  if (list.isError)
    return (
      <CardSection flush>
        <ErrorState title="Couldn't load revenue sources" error={list.error} />
      </CardSection>
    );
  return (
    <CardSection flush>
      <ul className="divide-y divide-line">
        {PROVIDERS.map((p) => (
          <SourceRow key={p} appId={appId} provider={p} source={by.get(p)} canManage={can("integrations.manage")} onDialog={setDialog} />
        ))}
      </ul>
      <p className="border-t border-line px-4 py-2.5 text-xs text-ink-3">
        A refund takes back a task completed in the 7 days before it. Give tasks that end in a subscription the undo step <InlineCode>subscription_canceled</InlineCode>.
      </p>
      <Modal
        open={dialog?.kind === "setup"}
        onOpenChange={(o) => !o && close()}
        title={dialog?.kind === "setup" ? `${current?.hasApiKey || current?.hasWebhook ? "Change" : "Connect"} ${REVENUE_PROVIDER_LABEL[dialog.provider]}` : ""}
        description={dialog?.kind === "setup" ? BLURB[dialog.provider] : undefined}
        wide
      >
        {dialog?.kind === "setup" && (dialog.provider === "stripe" ? <StripeSetup appId={appId} source={current} onDone={close} /> : <RevenueCatSetup appId={appId} source={current} onDone={close} />)}
      </Modal>
      <Modal
        open={dialog?.kind === "log"}
        onOpenChange={(o) => !o && close()}
        title={dialog?.kind === "log" ? `Received from ${REVENUE_PROVIDER_LABEL[dialog.provider]}` : ""}
        description="The last 50 events, newest first, and what each became."
        wide
      >
        {dialog?.kind === "log" && <RevenueLog appId={appId} provider={dialog.provider} currency={current?.currency ?? "USD"} />}
      </Modal>
    </CardSection>
  );
}

function SourceRow({ appId, provider, source, canManage, onDialog }: { appId: string; provider: RevenueProvider; source: RevenueSource | undefined; canManage: boolean; onDialog: (d: Dialog) => void }) {
  const name = REVENUE_PROVIDER_LABEL[provider];
  const status = sourceStatus(source);
  const invalidate = [["revenue", appId]];
  const remove = useApiMutation(() => api(`/apps/${appId}/revenue/${provider}`, { method: "DELETE" }), invalidate);
  const backfill = useApiMutation(() => api<RevenueSource>(`/apps/${appId}/revenue/stripe/backfill`, { method: "POST" }), invalidate);
  // drift-ok: only refetched by "Check now", whose failure goes to notifyError; RevenueSection shows a failed load.
  const recheck = useRevenueSources(appId);
  const [announce, setAnnounce] = useState("");
  const { confirm, dialog } = useConfirm();

  const check = async () => {
    const res = await recheck.refetch();
    if (res.isError) {
      notifyError(res.error);
      return;
    }
    const s = res.data?.find((x) => x.provider === provider);
    setAnnounce(`${name}: ${sourceStatus(s).text}.`);
  };

  const via = source ? [source.hasApiKey && (provider === "stripe" ? "restricted key" : "API key"), source.hasWebhook && "webhook"].filter(Boolean).join(" + ") : "";
  const waiting = source ? source.last7d.held + source.last7d.unmatched : 0;

  const actions: ActionMenuItem[] = source
    ? [
        { value: "log", label: "What arrived", onSelect: () => onDialog({ kind: "log", provider }) },
        ...(canManage
          ? [
              { value: "setup", label: "Change setup", onSelect: () => onDialog({ kind: "setup", provider }) },
              ...(provider === "stripe" && source.hasApiKey
                ? [
                    {
                      value: "backfill",
                      label: "Backfill the last 30 days",
                      onSelect: () =>
                        backfill.mutate(undefined, {
                          onSuccess: () => toaster.success({ title: "Backfilling the last 30 days", description: "Events already recorded aren't counted twice." }),
                          onError: notifyError,
                        }),
                    },
                  ]
                : []),
              {
                value: "remove",
                label: "Disconnect",
                danger: true,
                onSelect: async () => {
                  if (await confirm({ title: `Disconnect ${name}?`, description: "Nothing more is received, and its log and saved keys are deleted. Events already recorded stay.", confirmLabel: "Disconnect" }))
                    remove.mutate(undefined, { onSuccess: () => track("revenue_disconnected", { provider }), onError: notifyError });
                },
              },
            ]
          : []),
      ]
    : [];

  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5 px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink">
          {name}
          {source?.label && source.label !== name && <span className="font-normal text-ink-2"> · {source.label}</span>}
        </div>
        <div className="text-xs text-ink-3">{source ? `${via || "No key or webhook yet"} · revenue in ${source.currency}` : BLURB[provider]}</div>
      </div>
      <div className="col-start-2 row-span-2 flex items-center gap-2">
        {!source && canManage && (
          <Button size="sm" onClick={() => onDialog({ kind: "setup", provider })}>
            Connect<span className="sr-only"> {name}</span>
          </Button>
        )}
        {source && (
          <ActionMenu label={`Actions for ${name}`} items={actions} />
        )}
      </div>
      {source && (
        <div className="col-start-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <Badge tone={status.tone}>{status.text}</Badge>
          {source.lastEventAt && <span className="text-ink-3">{fmtNumber(source.last7d.recorded)} recorded in 7 days</span>}
          {waiting > 0 && (
            <Button variant="link" onClick={() => onDialog({ kind: "log", provider })}>
              {fmtNumber(waiting)} not tied to a person yet
            </Button>
          )}
          {source.backfill?.status === "running" && <span className="text-ink-3">Backfill: {fmtNumber(source.backfill.recorded)} recorded so far</span>}
          <Button variant="link" onClick={check} disabled={recheck.isFetching}>
            <RefreshCw size={12} aria-hidden /> Check for events
          </Button>
          {status.tone === "bad" && (source.lastRejected?.reason || source.backfill?.error) && (
            <span className="basis-full text-ink-2">{source.lastRejected && (!source.lastEventAt || source.lastRejected.at > source.lastEventAt) ? source.lastRejected.reason : source.backfill?.error}</span>
          )}
        </div>
      )}
      <span className="sr-only" role="status" aria-live="polite">
        {announce}
      </span>
      {dialog}
    </li>
  );
}

// --- Setup ---------------------------------------------------------------------------------------

function IdentitySnippets({ appId, provider }: { appId: string; provider: RevenueProvider }) {
  const app = useApp(appId);
  const methods = SNIPPET_METHODS[provider];
  const preferred = app.data?.platform === "ios" ? "ios" : app.data?.platform === "android" ? "android" : app.data?.platform === "flutter" ? "flutter" : app.data?.platform === "react-native" ? "reactNative" : app.data?.platform === "server" ? "http" : null;
  const [method, setMethod] = useState<InstallMethod>(preferred && methods.includes(preferred) ? preferred : methods[0]!);
  const s = REVENUE_SNIPPETS[method];
  return (
    <section aria-labelledby={`${provider}-identity`} className="space-y-2">
      <h3 id={`${provider}-identity`} className="text-sm font-semibold text-ink">
        Tie payments to people
      </h3>
      <p className="text-xs text-ink-2">
        {provider === "stripe"
          ? "Give Stripe the same user id you pass to identify(): Checkout's client_reference_id, or metadata.metrickle_user_id on the customer, subscription or PaymentIntent. Payments for a customer Metrickle has seen before are matched automatically."
          : "Log in to RevenueCat with the same user id you pass to identify(), or set the metrickle_user_id attribute. Purchases by anonymous RevenueCat ids wait until they're known."}
      </p>
      <TabBar items={methods.map((m) => ({ value: m, label: INSTALL_METHOD_LABEL[m] }))} value={method} onChange={(v) => setMethod(v as InstallMethod)} aria-label="SDK" panelId={`${provider}-identity-code`} />
      <div id={`${provider}-identity-code`} role="tabpanel" aria-label={`${INSTALL_METHOD_LABEL[method]} snippet`}>
        <CodeBlock code={s.code} label={INSTALL_METHOD_LABEL[method]} language={s.language} />
      </div>
    </section>
  );
}

function CommonFields({ currency, setCurrency, includeTest, setIncludeTest, provider }: { currency: string; setCurrency: (v: string) => void; includeTest: boolean; setIncludeTest: (v: boolean) => void; provider: RevenueProvider }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Report revenue in" hint="3-letter code. Other currencies are converted at the day's rate; use the one your other revenue events are in.">
        <Input required pattern="[A-Za-z]{3}" maxLength={3} autoComplete="off" className="w-28 uppercase" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
      </Field>
      <Checkbox checked={includeTest} onCheckedChange={setIncludeTest} hint={provider === "stripe" ? "Stripe test mode, for trying the setup. Turn it off after." : "Sandbox purchases, for trying the setup. Turn it off after."}>
        Record test events
      </Checkbox>
    </div>
  );
}

type SaveBody = { apiKey?: string | null; webhookSecret?: string | null; currency?: string; includeTest?: boolean; newAuthorization?: boolean };
type SaveResult = { source: RevenueSource; authorization: string | null; warnings: string[] };

function useSave(appId: string, provider: RevenueProvider) {
  return useApiMutation((b: SaveBody) => api<SaveResult>(`/apps/${appId}/revenue/${provider}`, { method: "PUT", json: b }), [["revenue", appId]]);
}

function StripeSetup({ appId, source, onDone }: { appId: string; source: RevenueSource | undefined; onDone: () => void }) {
  const save = useSave(appId, "stripe");
  // Its own mutation, so "Show the endpoint URL" and Save each show their own pending label.
  const hook = useSave(appId, "stripe");
  const [apiKey, setApiKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [currency, setCurrency] = useState(source?.currency ?? "USD");
  const [includeTest, setIncludeTest] = useState(source?.includeTest ?? false);
  const [hookUrl, setHookUrl] = useState(source?.hookUrl ?? null);

  // The endpoint URL comes first: Stripe shows the signing secret once the endpoint exists.
  const getUrl = () =>
    hook.mutate({ currency: currency || "USD", includeTest }, { onSuccess: (r) => setHookUrl(r.source.hookUrl), onError: notifyError });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await save.mutateAsync({ ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}), ...(webhookSecret.trim() ? { webhookSecret: webhookSecret.trim() } : {}), currency, includeTest });
      track("revenue_connected", { provider: "stripe", key: r.source.hasApiKey, webhook: r.source.hasWebhook });
      toaster.success({
        title: "Stripe connected",
        description: r.warnings.length ? r.warnings.join(" ") : r.source.backfill?.status === "running" ? "Backfilling the last 30 days now." : "Events show here as they arrive.",
      });
      onDone();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <section aria-labelledby="stripe-key" className="space-y-2">
        <h3 id="stripe-key" className="text-sm font-semibold text-ink">
          1. Restricted key <span className="font-normal text-ink-3">(recommended)</span>
        </h3>
        <p className="text-xs text-ink-2">
          In Stripe: Developers → API keys → Create restricted key. Give it <strong>Read</strong> on Charges, Refunds, Customers, Subscriptions, Checkout Sessions and Events, and nothing else. It backfills the last 30 days, finds who a customer is,
          and catches anything a webhook misses. It's encrypted and never shown again.
        </p>
        <Field label={source?.hasApiKey ? "Replace the restricted key" : "Restricted key"}>
          <Input type="password" autoComplete="off" spellCheck={false} className="font-mono" placeholder={source?.hasApiKey ? "Saved. Paste a new one to replace it" : "rk_live_…"} value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
        </Field>
      </section>

      <section aria-labelledby="stripe-webhook" className="space-y-2">
        <h3 id="stripe-webhook" className="text-sm font-semibold text-ink">
          2. Webhook <span className="font-normal text-ink-3">(for events within seconds)</span>
        </h3>
        {hookUrl ? (
          <>
            <p className="text-xs text-ink-2">In Stripe: Developers → Webhooks → Add destination. Use this endpoint URL and select these events, then paste the endpoint's signing secret below.</p>
            <CodeBlock code={hookUrl} label="Endpoint URL" />
            <CodeBlock code={STRIPE_EVENTS} label="Events to send" />
            <Field label={source?.hasWebhook ? "Replace the signing secret" : "Signing secret"}>
              <Input type="password" autoComplete="off" spellCheck={false} className="font-mono" placeholder={source?.hasWebhook ? "Saved. Paste a new one to replace it" : "whsec_…"} value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} />
            </Field>
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs text-ink-2">Get the endpoint URL to add in Stripe. Without a webhook, a restricted key reads new events every 15 minutes.</p>
            <Button type="button" size="sm" onClick={getUrl} disabled={save.isPending} pending={hook.isPending} pendingLabel="Showing…">
              Show the endpoint URL
            </Button>
          </div>
        )}
      </section>

      <CommonFields currency={currency} setCurrency={setCurrency} includeTest={includeTest} setIncludeTest={setIncludeTest} provider="stripe" />
      <IdentitySnippets appId={appId} provider="stripe" />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" disabled={save.isPending || hook.isPending || (!apiKey.trim() && !webhookSecret.trim() && !source)}>
          {save.isPending ? "Checking…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

function RevenueCatSetup({ appId, source, onDone }: { appId: string; source: RevenueSource | undefined; onDone: () => void }) {
  const save = useSave(appId, "revenuecat");
  // Its own mutation, so the webhook buttons and Save each show their own pending label.
  const hook = useSave(appId, "revenuecat");
  const [apiKey, setApiKey] = useState("");
  const [currency, setCurrency] = useState(source?.currency ?? "USD");
  const [includeTest, setIncludeTest] = useState(source?.includeTest ?? false);
  const [created, setCreated] = useState<{ url: string; authorization: string } | null>(null);
  const { confirm, dialog } = useConfirm();

  const issue = (newAuthorization: boolean) =>
    hook.mutate(
      { currency, includeTest, newAuthorization },
      { onSuccess: (r) => r.authorization && r.source.hookUrl && setCreated({ url: r.source.hookUrl, authorization: r.authorization }), onError: notifyError },
    );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await save.mutateAsync({ ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}), currency, includeTest });
      track("revenue_connected", { provider: "revenuecat", key: r.source.hasApiKey, webhook: r.source.hasWebhook });
      toaster.success({ title: "RevenueCat saved", description: "Send a test event from RevenueCat to check it arrives." });
      onDone();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <section aria-labelledby="rc-webhook" className="space-y-2">
        <h3 id="rc-webhook" className="text-sm font-semibold text-ink">
          1. Webhook
        </h3>
        {created ? (
          <>
            <p className="text-xs text-ink-2">
              In RevenueCat: Project settings → Integrations → Webhooks → Add new configuration. Paste the URL and the Authorization header value, choose Production (and Sandbox, to test), then send a test event. The Authorization value is
              shown once.
            </p>
            <CodeBlock code={created.url} label="Webhook URL" />
            <CodeBlock code={created.authorization} label="Authorization header value" />
          </>
        ) : source ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs text-ink-2">The webhook is set up. If you've lost the Authorization value, issue a new one and update it in RevenueCat.</p>
            <Button
              type="button"
              size="sm"
              disabled={save.isPending}
              pending={hook.isPending}
              pendingLabel="Issuing…"
              onClick={async () => {
                if (await confirm({ title: "Issue a new Authorization value?", description: "The current one stops working straight away. Update it in RevenueCat.", confirmLabel: "Issue new value" })) issue(true);
              }}
            >
              Issue a new Authorization value
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs text-ink-2">Metrickle makes a webhook URL and an Authorization value for RevenueCat to send.</p>
            <Button type="button" size="sm" onClick={() => issue(false)} disabled={save.isPending} pending={hook.isPending} pendingLabel="Creating…">
              Create webhook details
            </Button>
          </div>
        )}
      </section>

      <section aria-labelledby="rc-key" className="space-y-2">
        <h3 id="rc-key" className="text-sm font-semibold text-ink">
          2. Secret API key <span className="font-normal text-ink-3">(optional)</span>
        </h3>
        <p className="text-xs text-ink-2">A v2 secret key with read access names the connected project here. It's encrypted and never shown again.</p>
        <Field label={source?.hasApiKey ? "Replace the secret key" : "Secret API key"}>
          <Input type="password" autoComplete="off" spellCheck={false} className="font-mono" placeholder={source?.hasApiKey ? "Saved. Paste a new one to replace it" : "sk_…"} value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
        </Field>
      </section>

      <CommonFields currency={currency} setCurrency={setCurrency} includeTest={includeTest} setIncludeTest={setIncludeTest} provider="revenuecat" />
      <IdentitySnippets appId={appId} provider="revenuecat" />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          {created || source ? "Done" : "Cancel"}
        </Button>
        {(source || created) && (
          <Button variant="primary" disabled={save.isPending || hook.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        )}
      </div>
      {dialog}
    </form>
  );
}

// --- Log -----------------------------------------------------------------------------------------

function RevenueLog({ appId, provider, currency }: { appId: string; provider: RevenueProvider; currency: string }) {
  const log = useQuery({ queryKey: ["revenue-log", appId, provider], queryFn: () => api<RevenueDelivery[]>(`/apps/${appId}/revenue/${provider}/deliveries`) });
  if (log.isError) return <ErrorState title="Couldn't load what arrived" error={log.error} />;
  if (!log.data)
    return (
      <div aria-busy="true">
        <Skeleton className="h-32" />
        <span className="sr-only">Loading…</span>
      </div>
    );
  if (!log.data.length)
    return (
      <p className="text-sm text-ink-2">
        Nothing has arrived yet. {provider === "stripe" ? "Send a test webhook from Stripe (or run stripe trigger checkout.session.completed with test events on)." : "Use “Send test event” on the webhook in RevenueCat."}
      </p>
    );
  return (
    <div className="space-y-2">
      <TableScroll label="Received events" className="max-h-96 overflow-y-auto">
        <Table caption={`Events received from ${REVENUE_PROVIDER_LABEL[provider]}`} flush>
          <thead>
            <tr>
              <th scope="col" className="py-1.5">When</th>
              <th scope="col" className="py-1.5">{REVENUE_PROVIDER_LABEL[provider]} event</th>
              <th scope="col" className="py-1.5">Result</th>
            </tr>
          </thead>
          <tbody>
            {log.data.map((d) => (
              <tr key={d.id}>
                <td className="py-1.5 whitespace-nowrap text-ink-2">
                  <Ago t={d.at} />
                  {d.via === "api" && <span className="block text-xs text-ink-3">via API</span>}
                </td>
                <td className="py-1.5 font-mono text-xs text-ink">
                  {/* Break long names at their dots, not mid-word. */}
                  {d.type.split(".").map((part, i) => (
                    <span key={i}>
                      {i > 0 && "."}
                      {i > 0 && <wbr />}
                      {part}
                    </span>
                  ))}
                </td>
                <td className="py-1.5">
                  <Badge tone={STATUS_TONE[d.status]}>{STATUS_LABEL[d.status]}</Badge>
                  {d.event && (
                    <span className="ml-2 text-xs text-ink">
                      {REVENUE_EVENT_LABEL[d.event]?.label ?? d.event}
                      {d.amount !== null && ` ${fmtMoney(d.amount, currency)}`}
                    </span>
                  )}
                  {d.detail && <span className="mt-0.5 block text-xs text-ink-3">{d.detail}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
      <Disclosure summary="What each event becomes" size="xs" contentClassName="text-xs text-ink-2">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          {REVENUE_EVENTS.map((e) => (
            <div key={e} className="contents">
              <dt className="font-mono text-ink">{e}</dt>
              <dd>{REVENUE_EVENT_LABEL[e].hint}</dd>
            </div>
          ))}
        </dl>
      </Disclosure>
    </div>
  );
}
